/**
 * Lucide icon runtime
 *
 * Self-hosted rather than CDN-loaded: Lucide is not published on cdnjs (the only
 * script CDN our CSP allows), so a CDN tag would either 404 or require widening
 * script-src to a new third-party host. Bundling it keeps the version pinned by
 * package.json and served from our own origin.
 *
 * Converts <i data-lucide="name"> markup — emitted by the blank-shell starter
 * template and by cards declaring iconAdapter: 'lucide' — into inline SVG.
 */

import { createIcons, icons } from 'lucide';

function startObserving() {
    if (!observer) return;
    observer.observe(document.documentElement, {
        childList: true, subtree: true, attributes: true, attributeFilter: ['data-lucide']
    });
}

let observer = null;
let renderScheduled = false;

// createIcons() replaces each <i data-lucide> with an <svg>, which is itself a
// DOM mutation. Observing without pausing re-triggers the render from its own
// output and spins forever (observed live: it hung the page), so the observer is
// detached for the duration of every render and the work is coalesced per frame.
function renderLucideIcons() {
    if (renderScheduled) return;
    renderScheduled = true;
    const run = () => {
        renderScheduled = false;
        if (observer) observer.disconnect();
        try {
            createIcons({ icons });
        } catch (error) {
            console.warn('[LucideIcons] Failed to render icons:', error);
        } finally {
            if (observer) startObserving();
        }
    };
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(run);
    else setTimeout(run, 0);
}

if (typeof window !== 'undefined') {
    // Kept on the shared namespace per the project's global convention, with the
    // bare window.lucide alias for parity with the upstream UMD build.
    window.atomicfalls = window.atomicfalls || {};
    window.atomicfalls.lucideIcons = { createIcons: renderLucideIcons, icons };
    window.lucide = window.lucide || { createIcons: renderLucideIcons, icons };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', renderLucideIcons);
    } else {
        renderLucideIcons();
    }

    // Card edits rewrite data-lucide on existing nodes (customShellCardBridge's
    // applyDefaultValue), and sections render asynchronously after first paint,
    // so re-render when new or changed icon markup appears rather than assuming
    // a single pass at load is enough. Only unrendered <i> markup counts —
    // matching on any data-lucide node would also match Lucide's own output.
    observer = new MutationObserver((mutations) => {
        const needsRender = mutations.some((mutation) => (
            (mutation.type === 'attributes' && mutation.target?.matches?.('i[data-lucide]'))
            || Array.from(mutation.addedNodes || []).some((node) => (
                node.nodeType === 1
                && (node.matches?.('i[data-lucide]') || node.querySelector?.('i[data-lucide]'))
            ))
        ));
        if (needsRender) renderLucideIcons();
    });
    startObserving();
}

export { renderLucideIcons, icons };
